import { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Button,
  Input,
} from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UpdateSupervisor, PostSupervisorCompany } from "../../actions/teacherMutations";
import { GetSupervisorCompanies } from "../../actions/teacherQueries";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ButtonSendActivationMail from "../Button/ButtonSendActivationMail";
import { compareByName } from "../../Utilities/sortHelper";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";

export default function EditSupervisorsModal({ isOpen, toggle, supervisors }) {
  const intl = useIntl();
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [companyId, setCompanyId] = useState(0);
  const [addNewCompany, setAddNewCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");

  const updateSupervisorMutation = useMutation({ mutationFn: UpdateSupervisor });
  const postCompanyMutation = useMutation({ mutationFn: PostSupervisorCompany });
  const companies = GetSupervisorCompanies();
  const queryClient = useQueryClient();

  const startEditing = (sv) => {
    setEditingId(sv.id);
    setName(sv.name);
    setEmail(sv.email);
    setPhone(sv.phone || "");
    setCompanyId(sv.aplCompany?.id || 0);
    setAddNewCompany(false);
    setNewCompanyName("");
  };

  const cancelEditing = () => {
    setEditingId(null);
    setAddNewCompany(false);
    setNewCompanyName("");
  };

  const hasChanged = (sv) => {
    return (
      name !== sv.name ||
      email !== sv.email ||
      phone !== (sv.phone || "") ||
      companyId !== (sv.aplCompany?.id || 0)
    );
  };

  const handleSave = (sv) => {
    if (!name.trim()) {
      toast.warn(intl.formatMessage({ id: "NameRequired" }));
      return;
    }
    if (!email.trim()) {
      toast.warn(intl.formatMessage({ id: "EmailRequired" }));
      return;
    }
    if (!companyId || companyId <= 0) {
      toast.warn(intl.formatMessage({ id: "SupervisorNeedsCompany" }));
      return;
    }

    updateSupervisorMutation.mutate(
      {
        Id: sv.id,
        Name: name,
        Email: email,
        Phone: phone,
        CompanyId: companyId,
      },
      {
        onSuccess: () => {
          toast.success("Handledare uppdaterad");
          queryClient.invalidateQueries({ queryKey: ["FetchSupervisors"] });
          setEditingId(null);
        },
        onError: (err) => {
          toast.error(formatServiceError(intl, err?.response?.data));
        },
      }
    );
  };

  const handleCompanyChange = (value) => {
    if (value === "Add") {
      setAddNewCompany(true);
      setNewCompanyName("");
    } else {
      setCompanyId(Number(value));
    }
  };

  const handleSubmitNewCompany = () => {
    const cleanedCompanyName = newCompanyName.replace("&", "%26");
    postCompanyMutation.mutate(cleanedCompanyName, {
      onSuccess: (data) => {
        toast.success(intl.formatMessage({ id: "CompanyAdded" }));
        queryClient.invalidateQueries({ queryKey: ["FetchSupervisorCompanies"] });
        setCompanyId(data.data.result.id);
        setAddNewCompany(false);
        setNewCompanyName("");
      },
      onError: (err) => {
        toast.warn(formatServiceError(intl, err?.response?.data));
        setAddNewCompany(false);
        setNewCompanyName("");
      },
    });
  };

  const handleClose = () => {
    cancelEditing();
    toggle();
  };

  const displayEmail = (supervisorEmail) => {
    return supervisorEmail?.includes("_apl_spv")
      ? supervisorEmail.replace("_apl_spv", "")
      : supervisorEmail;
  };

  const lowerSearch = searchTerm.toLowerCase();
  const filtered = [...(supervisors || [])]
    .filter((sv) =>
      !searchTerm ||
      sv.name.toLowerCase().includes(lowerSearch) ||
      sv.email.toLowerCase().includes(lowerSearch) ||
      (sv.aplCompany?.name || "").toLowerCase().includes(lowerSearch)
    )
    .sort(compareByName);

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="xl">
      <ModalHeader toggle={handleClose}>
        <FormattedMessage id="EditSupervisors" />
      </ModalHeader>
      <ModalBody>
        <Input
          type="text"
          placeholder={intl.formatMessage({ id: "SearchNameEmailCompany" })}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="mb-3"
        />
        <Table responsive size="sm" className="mb-0" borderless hover>
          <thead className="bg-light">
            <tr>
              <th className="ps-3 py-2 text-muted">
                <FormattedMessage id="Name" />
              </th>
              <th className="py-2 text-muted">
                <FormattedMessage id="Email" />
              </th>
              <th className="py-2 text-muted" style={{ width: "130px" }}>
                <FormattedMessage id="Phone" />
              </th>
              <th className="py-2 text-muted text-center" style={{ width: "100px" }}>
                <FormattedMessage id="Activated" />
              </th>
              <th className="py-2 text-muted">
                <FormattedMessage id="Company" />
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((sv) =>
              editingId === sv.id ? (
                <tr key={sv.id}>
                  <td className="ps-3 align-middle">
                    <Input
                      bsSize="sm"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </td>
                  <td className="align-middle">
                    <Input
                      bsSize="sm"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </td>
                  <td className="align-middle">
                    <Input
                      bsSize="sm"
                      type="text"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </td>
                  <td className="align-middle" onClick={(e) => e.stopPropagation()}>
                    <div className="d-flex justify-content-center">
                      {sv.activatedAccount ? (
                        <span title={intl.formatMessage({ id: "Activated" })}>
                          <FontAwesomeIcon
                            icon="check"
                            className="text-success"
                          />
                        </span>
                      ) : (
                        <ButtonSendActivationMail user={sv} />
                      )}
                    </div>
                  </td>
                  <td className="align-middle">
                    {addNewCompany ? (
                      <div className="d-flex align-items-center">
                        <Input
                          bsSize="sm"
                          type="text"
                          value={newCompanyName}
                          onChange={(e) => setNewCompanyName(e.target.value)}
                          placeholder={intl.formatMessage({ id: "CompanyName" })}
                        />
                        <Button
                          size="sm"
                          color="primary"
                          outline
                          className="ms-1"
                          onClick={handleSubmitNewCompany}
                          disabled={!newCompanyName.trim()}
                        >
                          <FontAwesomeIcon icon="check" />
                        </Button>
                        <Button
                          size="sm"
                          color="secondary"
                          outline
                          className="ms-1"
                          onClick={() => setAddNewCompany(false)}
                        >
                          <FontAwesomeIcon icon="xmark" />
                        </Button>
                      </div>
                    ) : (
                      <Input
                        bsSize="sm"
                        type="select"
                        value={companyId}
                        onChange={(e) => handleCompanyChange(e.target.value)}
                      >
                        <option value="0">{intl.formatMessage({ id: "ChooseCompany" })}</option>
                        <option value="Add">{intl.formatMessage({ id: "AddNewCompany" })}</option>
                        {[...(companies.data ?? [])]
                          .sort(compareByName)
                          .map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                      </Input>
                    )}
                  </td>
                  <td className="align-middle text-end text-nowrap">
                    {hasChanged(sv) && (
                      <Button
                        color="primary"
                        outline
                        size="sm"
                        className="me-1"
                        disabled={updateSupervisorMutation.isPending}
                        onClick={() => handleSave(sv)}
                      >
                        <FontAwesomeIcon icon="floppy-disk" />
                      </Button>
                    )}
                    <Button
                      color="secondary"
                      outline
                      size="sm"
                      onClick={cancelEditing}
                    >
                      <FontAwesomeIcon icon="xmark" />
                    </Button>
                  </td>
                </tr>
              ) : (
                <tr
                  key={sv.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => startEditing(sv)}
                >
                  <td className="ps-3 align-middle">{sv.name}</td>
                  <td className="align-middle">{displayEmail(sv.email)}</td>
                  <td className="align-middle">{sv.phone}</td>
                  <td className="align-middle" onClick={(e) => e.stopPropagation()}>
                    <div className="d-flex justify-content-center">
                      {sv.activatedAccount ? (
                        <span title={intl.formatMessage({ id: "Activated" })}>
                          <FontAwesomeIcon
                            icon="check"
                            className="text-success"
                          />
                        </span>
                      ) : (
                        <ButtonSendActivationMail user={sv} />
                      )}
                    </div>
                  </td>
                  <td className="align-middle">{sv.aplCompany?.name}</td>
                </tr>
              )
            )}
          </tbody>
        </Table>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={handleClose}>
          <FormattedMessage id="Close" />
        </Button>
      </ModalFooter>
    </Modal>
  );
}
